/**
 * Topbar enxuta do PharuERP — Fase 2 do plano-mestre.
 *
 * Substitui o Header antigo que tinha seletor de modulos + dropdowns de
 * grupos. Toda a navegacao por modulos/grupos foi para a Sidebar persistente
 * a esquerda. O Topbar agora carrega:
 *   - Burger (so mobile) que abre a Sidebar como drawer
 *   - Logo PharuERP (clicavel, leva para /home)
 *   - Pill "Atualizado em ..." da planilha (com botao de refresh)
 *   - Pill "Instalar app" (PWA, quando disponivel)
 *   - Toggle de precos
 *   - Menu do usuario
 *
 * Altura fixa de 56px (TOPBAR_HEIGHT em Sidebar/styles).
 */
import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiChevronDown,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiHelpCircle,
  FiLogOut,
  FiMenu,
  FiMoon,
  FiRefreshCw,
  FiSettings,
  FiSun,
  FiUser,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { rotaDeInicio } from "../../utils/rotas";
import { useDados } from "../../context/DadosContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { useThemeToggle } from "../../context/ThemeContext";
import { useAuthenticatedImageUrl } from "../../hooks/useAuthenticatedImageUrl";
import { useInstallPrompt } from "../../hooks/useInstallPrompt";
import useNotificacoesAgregadas from "../../hooks/useNotificacoesAgregadas";
import { PharuERPLogo, PharuERPSymbol } from "../Brand/PharuERPLogo";
import NotificationBell from "../NotificationBell";
import BotaoDoChat from "../BotaoDoChat";
import { pedirTour } from "../Tour/controle";
import { ConviteDosAvisosNoDesktop } from "../NotificacoesDoChat";
import {
  Actions,
  AvatarWrapper,
  BurgerButton,
  BotaoDeAjuda,
  HeaderWrapper,
  InstallPill,
  LogoArea,
  LogoCompact,
  LogoFull,
  PriceButton,
  StatusDot,
  TenantPill,
  ThemeToggleGroup,
  ThemeToggleSegment,
  UpdatePill,
  UserInitials,
  UserMenu,
  UserMenuButton,
  UserMenuItem,
  UserMenuWrapper,
  UserName,
  UserPhoto,
} from "./styles";

export default function Header({ onOpenSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canViewPrices, precosVisiveis, togglePrecos } = usePrecosVisiveis();
  const { logout, usuario, temModulo } = useAuth();
  const { isDark, setThemeMode } = useThemeToggle();
  // Indicador "Atualizado em ..." da planilha.
  const dadosCtx = useDados();
  const meta = dadosCtx?.meta;
  const recarregar = dadosCtx?.recarregarDados;
  const atualizandoPlanilha = dadosCtx?.alterandoPlanilha;
  const { imageUrl: profilePhotoUrl } = useAuthenticatedImageUrl(usuario?.foto_url);
  const { podeInstalar, instalar } = useInstallPrompt();
  // UI.3 (Bloco UI, auditoria 2026-05-23): dot indicator no avatar
  // refletindo total agregado de notificacoes pendentes.
  const { total: totalNotificacoes, chat } = useNotificacoesAgregadas();

  const [userMenuAberto, setUserMenuAberto] = useState(false);
  const userMenuWrapperRef = useRef(null);

  useEffect(() => {
    setUserMenuAberto(false);
  }, [location.pathname]);

  // Click-outside para fechar o dropdown do usuario. Antes o popup
  // soh fechava ao clicar de novo no proprio botao — UX ruim.
  // Padrao replicado do ModuleRailItem em Sidebar/index.jsx.
  useEffect(() => {
    if (!userMenuAberto) return undefined;
    const onMouseDown = (event) => {
      const wrapper = userMenuWrapperRef.current;
      if (wrapper && !wrapper.contains(event.target)) {
        setUserMenuAberto(false);
      }
    };
    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [userMenuAberto]);

  const initials = String(usuario?.nome || usuario?.email || "U")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  const displayName = usuario?.nome || usuario?.email || "Usuário";

  // MT-4.7 (multi-tenant): unidade corrente do payload do /api/me
  // (business_unit_codigo/business_unit_nome). Render defensivo — sessões
  // antigas (antes do backend expor a unidade) simplesmente não mostram
  // a pill até o próximo loadMe.
  const tenantCodigo = usuario?.business_unit_codigo || "";
  const tenantNome = usuario?.business_unit_nome || "";
  const tenantLabel = [tenantCodigo, tenantNome].filter(Boolean).join(" — ");

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  function formatarUltimaAtualizacao(timestamp) {
    if (!timestamp) return null;
    try {
      const data = new Date(timestamp);
      if (Number.isNaN(data.getTime())) return null;
      const dia = String(data.getDate()).padStart(2, "0");
      const mes = String(data.getMonth() + 1).padStart(2, "0");
      const hora = String(data.getHours()).padStart(2, "0");
      const min = String(data.getMinutes()).padStart(2, "0");
      return `${dia}/${mes} ${hora}:${min}`;
    } catch {
      return null;
    }
  }

  const ultimaAtualizacao = formatarUltimaAtualizacao(meta?.timestamp);

  async function handleRecarregar() {
    if (typeof recarregar === "function") {
      try {
        await recarregar();
      } catch {
        /* erro tratado pelo provider via planilhaErro */
      }
    }
  }

  return (
    <HeaderWrapper>
      <BurgerButton
        type="button"
        onClick={onOpenSidebar}
        aria-label="Abrir menu"
        data-tour="abrir-menu"
      >
        <FiMenu size={20} />
      </BurgerButton>

      <LogoArea to={rotaDeInicio(temModulo)} aria-label="PharuERP — ir para Início">
        <LogoFull>
          <PharuERPLogo symbolSize={28} wordmarkHeight={18} gap={10} />
        </LogoFull>
        <LogoCompact>
          <PharuERPSymbol size={28} />
        </LogoCompact>
      </LogoArea>

      <Actions>
        {podeInstalar && (
          <InstallPill
            type="button"
            onClick={instalar}
            title="Instalar o PharuERP como aplicativo no seu computador"
            aria-label="Instalar o PharuERP como aplicativo"
          >
            <FiDownload size={12} />
            <span>Instalar app</span>
          </InstallPill>
        )}
        {ultimaAtualizacao && (
          <UpdatePill
            type="button"
            onClick={handleRecarregar}
            disabled={atualizandoPlanilha}
            title="Atualizar dados da planilha"
            aria-label={`Dados de ${ultimaAtualizacao}. Clique para atualizar.`}
          >
            <FiRefreshCw size={12} />
            <span>Atualizado: {ultimaAtualizacao}</span>
          </UpdatePill>
        )}

        {/* UI.3 (Bloco UI, auditoria 2026-05-23): Help icon — primeiro elemento
            da direita. Abre o tour guiado de novo (ADR-0104). */}
        <BotaoDeAjuda
          type="button"
          onClick={pedirTour}
          aria-label="Ajuda: rever o tour do PharuERP"
          title="Ajuda: rever o tour do PharuERP"
          data-tour="ajuda"
        >
          <FiHelpCircle size={16} />
        </BotaoDeAjuda>

        {/* UI.1 (Bloco UI, auditoria 2026-05-23): Sun/Moon toggle.
            ThemeContext ja existe — so faltava UI. Segmented control com
            dois IconButtons (sol/lua), ativo highlighted em accent. */}
        <ThemeToggleGroup role="group" aria-label="Alternar tema claro/escuro">
          <ThemeToggleSegment
            type="button"
            $active={!isDark}
            onClick={() => setThemeMode("light")}
            aria-label="Tema claro"
            aria-pressed={!isDark}
            title="Tema claro"
          >
            <FiSun size={14} />
          </ThemeToggleSegment>
          <ThemeToggleSegment
            type="button"
            $active={isDark}
            onClick={() => setThemeMode("dark")}
            aria-label="Tema escuro"
            aria-pressed={isDark}
            title="Tema escuro"
          >
            <FiMoon size={14} />
          </ThemeToggleSegment>
        </ThemeToggleGroup>

        {/* UI.2 (Bloco UI, auditoria 2026-05-23): NotificationBell agregada.
            Mostra contagem de pendencias acionaveis (WhatsApp + assistencias
            atrasadas + contratos pendentes + OSEs atrasadas), filtrada por
            permissao. Click abre dropdown clicavel com lista. */}
        {chat?.permitido && <ConviteDosAvisosNoDesktop />}
        {chat?.permitido && (
          <span data-tour="chat" style={{ display: "inline-flex" }}>
            <BotaoDoChat conversasNaoLidas={chat.conversas} mencoes={chat.mencoes} />
          </span>
        )}

        <span data-tour="notificacoes" style={{ display: "inline-flex" }}>
          <NotificationBell />
        </span>

        {/* Bugfix 2026-05-28 (#5 retrabalho): usuario sem permissao de
            precos NAO deve nem ver o botao de olho — antes ficava
            disabled mas visivel, ainda chamando atencao. Agora se
            !canViewPrices, o botao simplesmente nao renderiza. */}
        {canViewPrices && (
          <PriceButton
            type="button"
            onClick={togglePrecos}
            aria-label={
              precosVisiveis
                ? "Ocultar valores monetários"
                : "Mostrar valores monetários"
            }
            title={
              precosVisiveis
                ? "Ocultar valores monetários"
                : "Mostrar valores monetários"
            }
          >
            {precosVisiveis ? <FiEye size={17} /> : <FiEyeOff size={17} />}
          </PriceButton>
        )}

        {tenantLabel && (
          <TenantPill title={`Unidade corrente: ${tenantLabel}`}>
            {tenantLabel}
          </TenantPill>
        )}

        <UserMenuWrapper ref={userMenuWrapperRef}>
          <UserMenuButton
            type="button"
            onClick={() => setUserMenuAberto((current) => !current)}
            aria-expanded={userMenuAberto}
            data-tour="menu-usuario"
            title={displayName}
          >
            <AvatarWrapper>
              {profilePhotoUrl ? (
                <UserPhoto src={profilePhotoUrl} alt={displayName} />
              ) : (
                <UserInitials>{initials}</UserInitials>
              )}
              {totalNotificacoes > 0 && (
                <StatusDot aria-label={`${totalNotificacoes} pendencia(s)`} />
              )}
            </AvatarWrapper>
            <UserName>{displayName}</UserName>
            <motion.span animate={{ rotate: userMenuAberto ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <FiChevronDown size={13} />
            </motion.span>
          </UserMenuButton>

          <AnimatePresence>
            {userMenuAberto && (
              <UserMenu
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
              >
                <UserMenuItem type="button" onClick={() => navigate("/configuracoes/perfil")}>
                  <FiUser size={14} />
                  Perfil
                </UserMenuItem>
                <UserMenuItem type="button" onClick={() => navigate("/configuracoes")}>
                  <FiSettings size={14} />
                  Configuracoes
                </UserMenuItem>
                <UserMenuItem
                  type="button"
                  onClick={() => {
                    setUserMenuAberto(false);
                    pedirTour();
                  }}
                >
                  <FiHelpCircle size={14} />
                  Rever o tour
                </UserMenuItem>
                <UserMenuItem type="button" onClick={handleLogout}>
                  <FiLogOut size={14} />
                  Sair
                </UserMenuItem>
              </UserMenu>
            )}
          </AnimatePresence>
        </UserMenuWrapper>
      </Actions>
    </HeaderWrapper>
  );
}
